import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import GuestPassModal from '../components/GuestPassModal';

interface Membership {
  id: string;
  status: string;
  startDate: string;
  endDate: string | null;
  plan: {
    id: string;
    name: string;
    features?: any;
  };
}

export default function MemberSelfServicePage() {
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMembership, setSelectedMembership] = useState<Membership | null>(null);
  const [showGuestPassModal, setShowGuestPassModal] = useState(false);

  useEffect(() => {
    fetchMemberships();
  }, []);

  const fetchMemberships = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/member/self-service/memberships`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMemberships(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching memberships:', error);
      toast.error('Failed to load memberships');
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (membershipId: string) => {
    if (!confirm('Are you sure you want to pause this membership?')) {
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/member/self-service/pause`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          membershipId,
          startDate: new Date().toISOString(),
          reason: 'Self-service pause request'
        })
      });

      if (response.ok) {
        toast.success('Membership paused successfully');
        fetchMemberships();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to pause membership');
      }
    } catch (error) {
      console.error('Error pausing membership:', error);
      toast.error('Failed to pause membership');
    }
  };

  const handleUnpause = async (membershipId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/member/self-service/unpause`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ membershipId })
      });

      if (response.ok) {
        toast.success('Membership unpaused successfully');
        fetchMemberships();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to unpause membership');
      }
    } catch (error) {
      console.error('Error unpausing membership:', error);
      toast.error('Failed to unpause membership');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">My Memberships</h1>
        <p className="text-gray-600 mt-1">Manage your membership and features</p>
      </div>

      {memberships.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <p className="text-gray-500">You don't have any active memberships.</p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {memberships.map((membership) => (
            <div key={membership.id} className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{membership.plan.name}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium mt-2 inline-block ${
                    membership.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : membership.status === 'paused'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {membership.status}
                  </span>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Start Date:</span>
                  <span className="text-gray-900">{new Date(membership.startDate).toLocaleDateString()}</span>
                </div>
                {membership.endDate && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">End Date:</span>
                    <span className="text-gray-900">{new Date(membership.endDate).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              {membership.plan.features && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Available Features:</h4>
                  <div className="flex flex-wrap gap-2">
                    {membership.plan.features.pauseEnabled && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">⏸️ Pause</span>
                    )}
                    {membership.plan.features.guestPasses?.enabled && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">👥 Guest Passes</span>
                    )}
                    {membership.plan.features.selfServiceEnabled?.pause && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">🔧 Self-Service</span>
                    )}
                  </div>
                </div>
              )}

              <div className="flex space-x-2 pt-4 border-t border-gray-100">
                {membership.status === 'active' && membership.plan.features?.selfServiceEnabled?.pause && (
                  <button
                    onClick={() => handlePause(membership.id)}
                    className="flex-1 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors text-sm font-medium"
                  >
                    Pause Membership
                  </button>
                )}
                {membership.status === 'paused' && (
                  <button
                    onClick={() => handleUnpause(membership.id)}
                    className="flex-1 px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
                  >
                    Resume Membership
                  </button>
                )}
                {membership.plan.features?.guestPasses?.enabled && (
                  <button
                    onClick={() => {
                      setSelectedMembership(membership);
                      setShowGuestPassModal(true);
                    }}
                    className="flex-1 px-4 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
                  >
                    Guest Passes
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Guest Pass Modal */}
      {selectedMembership && (
        <GuestPassModal
          isOpen={showGuestPassModal}
          onClose={() => {
            setShowGuestPassModal(false);
            setSelectedMembership(null);
          }}
          onSuccess={() => {
            fetchMemberships();
          }}
          membershipId={selectedMembership.id}
        />
      )}
    </div>
  );
}

